import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaGift, FaTimes } from 'react-icons/fa';

import { useGetDepositAccountsQuery } from '@/store/api/accountApi';
import { useGetBonusesQuery, type BonusOffer } from '@/store/api/bonusApi';
import { formatNumber } from '@/utils';

/**
 * "How much?" — the step in front of the cashier.
 *
 * The reference opens this the moment Deposit is tapped, and only then goes on
 * to the payment rails. `/user/deposit` already owns the rails (UPI, bank,
 * SGPay24, crypto), so this sheet does not replace that page: it picks the
 * amount, shows what the current campaign would add to it, and hands both to
 * that page through `?amount=`.
 *
 * Every number here comes from the operator's own configuration — the limits
 * from the deposit accounts they enabled, the bonus from the campaign they
 * created. Nothing is a written-in figure: an amount ladder that promised more
 * than a rail accepts, or a bonus nobody configured, would be an offer the
 * cashier then refuses.
 */

/** The ladder to draw from, filtered to what the operator's rails accept. */
const AMOUNT_LADDER = [100, 200, 500, 1000, 2000, 3000, 5000, 9000, 10000, 15000, 20000, 30000, 50000];

/** How many tiles the grid shows — three rows of two, as on the reference. */
const TILE_COUNT = 6;

const DepositModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const navigate = useNavigate();
  const { data: accounts } = useGetDepositAccountsQuery(undefined, { skip: !open });
  const { data: bonusData } = useGetBonusesQuery(
    { category: 'DepositBonus', status: 'Active', limit: 20 },
    { skip: !open },
  );

  const [amount, setAmount] = useState('');

  // Limits are the tightest each rail will take: the floor is the lowest an
  // enabled account accepts (below it nothing would go through) and the ceiling
  // the highest any of them does.
  const { min, max } = useMemo(() => {
    const all = [
      ...(accounts?.upi ?? []),
      ...(accounts?.bank ?? []),
      ...(accounts?.crypto ?? []),
    ] as { status?: string; minAmount?: number | string | null; maxAmount?: number | string | null }[];

    const live = all.filter((a) => (a.status ?? 'Active') === 'Active');
    const mins = live.map((a) => Number(a.minAmount)).filter((n) => Number.isFinite(n) && n > 0);
    const maxes = live.map((a) => Number(a.maxAmount)).filter((n) => Number.isFinite(n) && n > 0);

    return {
      min: mins.length ? Math.min(...mins) : null,
      max: maxes.length ? Math.max(...maxes) : null,
    };
  }, [accounts]);

  const tiles = useMemo(() => {
    const fits = AMOUNT_LADDER.filter(
      (v) => (min === null || v >= min) && (max === null || v <= max),
    );
    // Nothing in the ladder sits inside the operator's window — fall back to the
    // window itself rather than drawing tiles the cashier would reject.
    if (!fits.length) return min !== null ? [min] : [];
    return fits.slice(0, TILE_COUNT);
  }, [min, max]);

  // The campaign that actually applies to this amount. Several can be active at
  // once (a 100% up to ₹1,000 beside a 50% up to ₹10,000); the one that pays
  // most on what the player typed is the one worth showing.
  const value = Number(amount) || 0;
  const offer = useMemo(() => {
    const now = Date.now();
    const live = (bonusData?.bonuses ?? []).filter(
      (b) =>
        b.status === 'Active' &&
        b.percentage &&
        (!b.startDate || new Date(b.startDate).getTime() <= now) &&
        (!b.endDate || new Date(b.endDate).getTime() >= now),
    );
    const priced = live
      .filter((b) => value >= (b.minDepositAmount ?? 0))
      .map((b) => ({ bonus: b, award: awardFor(b, value) }))
      .sort((a, b) => b.award - a.award);
    return priced[0] ?? null;
  }, [bonusData, value]);

  // Body stays put while the sheet is open, and Escape closes it — same as the
  // auth sheets, so every full-screen layer on the site behaves alike.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const tooLow = min !== null && value > 0 && value < min;
  const tooHigh = max !== null && value > max;
  const canSubmit = value > 0 && !tooLow && !tooHigh;

  const submit = () => {
    if (!canSubmit) return;
    navigate(`/user/deposit?amount=${value}`);
    onClose();
  };

  return createPortal(
    <div className="dm-backdrop" onClick={onClose} role="presentation">
      <style>{CSS}</style>
      <div
        className="dm-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Deposit"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dm-head">
          <button className="dm-head-btn" onClick={onClose} aria-label="Back">
            <FaArrowLeft size={15} />
          </button>
          <h2 className="dm-title">Deposit</h2>
          <button className="dm-head-btn" onClick={onClose} aria-label="Close">
            <FaTimes size={15} />
          </button>
        </header>

        <div className="dm-body">
          <p className="dm-lede">Please choose or enter a deposit amount</p>

          {tiles.length > 0 && (
            <div className="dm-tiles">
              {tiles.map((v) => {
                const award = offerAwardAt(bonusData?.bonuses ?? [], v);
                return (
                  <button
                    key={v}
                    className={`dm-tile${value === v ? ' active' : ''}`}
                    onClick={() => setAmount(String(v))}
                  >
                    <span className="dm-tile-amt">{formatNumber(v)} INR</span>
                    {award > 0 && (
                      <span className="dm-tile-bonus">+ {formatNumber(award)} INR</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <label className="dm-label" htmlFor="dm-amount">Enter amount</label>
          <div className="dm-field">
            <input
              id="dm-amount"
              className="dm-input"
              inputMode="numeric"
              placeholder={min !== null ? String(min) : '0'}
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
            />
            <span className="dm-cur">INR</span>
          </div>

          {max !== null && (
            <p className="dm-limit">
              Max. deposit: <b>{formatNumber(max)} INR</b>
            </p>
          )}
          {tooLow && (
            <p className="dm-err">Minimum deposit is {formatNumber(min!)} INR.</p>
          )}
          {tooHigh && (
            <p className="dm-err">Maximum deposit is {formatNumber(max!)} INR.</p>
          )}

          {offer && offer.award > 0 && (
            <>
              <div className="dm-bonus">
                <div>
                  <p className="dm-bonus-cap">Bonus</p>
                  <p className="dm-bonus-amt">+{formatNumber(offer.award)} INR</p>
                </div>
                <FaGift className="dm-bonus-ico" size={26} />
              </div>
              <p className="dm-bonus-bar">On Bonus Balance</p>
            </>
          )}

          <button className="dm-cta" onClick={submit} disabled={!canSubmit}>
            Deposit
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** What one campaign pays on an amount: the percentage, capped. */
function awardFor(bonus: BonusOffer, amount: number): number {
  if (!bonus.percentage || amount <= 0) return 0;
  const raw = (amount * bonus.percentage) / 100;
  const capped = bonus.maxBonusAmount ? Math.min(raw, bonus.maxBonusAmount) : raw;
  return Math.floor(capped);
}

/** The best award across every live campaign, for a tile's own amount. */
function offerAwardAt(bonuses: BonusOffer[], amount: number): number {
  const now = Date.now();
  return bonuses
    .filter(
      (b) =>
        b.status === 'Active' &&
        amount >= (b.minDepositAmount ?? 0) &&
        (!b.startDate || new Date(b.startDate).getTime() <= now) &&
        (!b.endDate || new Date(b.endDate).getTime() >= now),
    )
    .reduce((best, b) => Math.max(best, awardFor(b, amount)), 0);
}

const CSS = `
  /* A bottom sheet: it rises from the edge the thumb is already at, and the
     amount field lands above the keyboard instead of behind it. */
  .dm-backdrop {
    position: fixed;
    inset: 0;
    z-index: 200;
    background: rgba(17,17,16,.62);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    overflow: hidden;
    animation: dmFade .18s ease-out;
  }
  @keyframes dmFade { from { opacity: 0 } to { opacity: 1 } }

  .dm-sheet {
    width: 100%;
    max-width: 480px;
    /* Never taller than the viewport; the body below scrolls if the offer and
       the tiles together outgrow it. */
    max-height: 92dvh;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: var(--cb-radius-m, .75rem) var(--cb-radius-m, .75rem) 0 0;
    box-shadow: 0 -12px 40px rgba(17,17,16,.3);
    overflow: hidden;
    animation: dmRise .26s cubic-bezier(.2,.8,.3,1);
  }
  @keyframes dmRise {
    from { transform: translateY(100%) }
    to   { transform: none }
  }
  @media (prefers-reduced-motion: reduce) {
    .dm-sheet { animation: none; }
  }

  .dm-head {
    flex-shrink: 0;
    display: grid;
    grid-template-columns: 32px 1fr 32px;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid var(--cb-line, #eeecec);
  }
  .dm-head-btn {
    display: flex; align-items: center; justify-content: center;
    width: 30px; height: 30px;
    border: none; background: none;
    color: rgba(52,49,49,.6);
    cursor: pointer;
  }
  .dm-head-btn:hover { color: var(--cb-ink, #343131); }
  .dm-title { margin: 0; text-align: center; font-size: 16px; font-weight: 800; }

  .dm-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 14px 14px calc(18px + env(safe-area-inset-bottom, 0px));
  }
  .dm-lede { margin: 0 0 12px; font-size: 13px; color: rgba(52,49,49,.72); }

  .dm-tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 9px;
    margin-bottom: 16px;
  }
  .dm-tile {
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    padding: 11px 8px;
    background: var(--cb-surface, #f5f5f5);
    border: 1.5px solid transparent;
    border-radius: var(--cb-radius-s, .5rem);
    cursor: pointer;
    transition: border-color .15s, background .15s;
  }
  .dm-tile:hover  { background: #ececec; }
  .dm-tile.active { border-color: var(--cb-ink-strong, #111110); background: #fff; }
  .dm-tile-amt   { font-size: 13.5px; font-weight: 700; color: var(--cb-ink, #343131); }
  .dm-tile-bonus { font-size: 12px; font-weight: 700; color: var(--cb-yellow-dark, #d39906); }

  .dm-label { display: block; margin-bottom: 6px; font-size: 12.5px; color: rgba(52,49,49,.72); }
  .dm-field {
    display: flex; align-items: center;
    background: var(--cb-surface, #f5f5f5);
    border: 1px solid var(--cb-line, #eeecec);
    border-radius: var(--cb-radius-s, .5rem);
    overflow: hidden;
  }
  .dm-field:focus-within { border-color: var(--cb-yellow, #ffb700); }
  .dm-input {
    flex: 1; min-width: 0;
    padding: 12px;
    background: none; border: none; outline: none;
    font-size: 14px; font-weight: 700;
    color: var(--cb-ink, #343131);
  }
  .dm-input::placeholder { color: rgba(52,49,49,.35); font-weight: 500; }
  .dm-cur { padding: 0 12px; font-size: 13px; font-weight: 700; color: rgba(52,49,49,.55); }

  .dm-limit { margin: 8px 0 0; font-size: 12px; color: rgba(52,49,49,.6); }
  .dm-limit b { color: var(--cb-green, #16b14a); font-weight: 800; }
  .dm-err { margin: 8px 0 0; font-size: 12px; font-weight: 700; color: #e02424; }

  .dm-bonus {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 14px; padding: 11px 12px;
    background: var(--cb-surface, #f5f5f5);
    border-radius: var(--cb-radius-s, .5rem) var(--cb-radius-s, .5rem) 0 0;
  }
  .dm-bonus-cap { margin: 0; font-size: 11.5px; color: rgba(52,49,49,.6); }
  .dm-bonus-amt { margin: 2px 0 0; font-size: 15px; font-weight: 800; color: var(--cb-yellow-dark, #d39906); }
  .dm-bonus-ico { color: var(--cb-yellow-dark, #d39906); flex-shrink: 0; }
  .dm-bonus-bar {
    margin: 0;
    padding: 8px 12px;
    background: var(--cb-yellow-dark, #d39906);
    border-radius: 0 0 var(--cb-radius-s, .5rem) var(--cb-radius-s, .5rem);
    color: #fff;
    font-size: 12px; font-weight: 800;
  }

  .dm-cta {
    width: 100%;
    margin-top: 16px;
    padding: 13px;
    border: none;
    border-radius: var(--cb-radius-s, .5rem);
    background: var(--cb-yellow, #ffb700);
    color: var(--cb-ink, #343131);
    font-size: 14.5px; font-weight: 800;
    cursor: pointer;
    transition: filter .15s, transform .1s;
  }
  .dm-cta:hover:not(:disabled)  { filter: brightness(1.06); }
  .dm-cta:active:not(:disabled) { transform: scale(.99); }
  .dm-cta:disabled { opacity: .45; cursor: not-allowed; }
`;

export default DepositModal;
